import React, { useEffect, useState, useRef } from "react";
import { FaEye, FaTrashAlt } from "react-icons/fa";
import apiRoutes from "../../../api/apiRoutes";
import FiltroProductos from "./FiltroProductos";
import useEscClose from "../../useEscClose";
import VerProductosTarjeta from "./VerProductosTarjeta";
import EliminarProductos from "./EliminarProductos";
import { appFetchJson } from "../../../utils/http";

const ProductosTarjeta = () => {
  // Datos
  const [items, setItems] = useState([]);

  // Paginación (UI 1-based)
  const [paginaActual, setPaginaActual] = useState(1);
  const [porPagina, setPorPagina] = useState(30);

  const [cargando, setCargando] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  useEscClose(() => {
    setMostrarFiltros(false);
    document.activeElement?.blur();
  }, mostrarFiltros);

  // Modales
  const [showVer, setShowVer] = useState(false);
  const [itemSel, setItemSel] = useState(null);
  useEscClose(() => {
    setShowVer(false);
    document.activeElement?.blur();
  }, showVer);

  const [showDelete, setShowDelete] = useState(false);
  const [itemDelete, setItemDelete] = useState(null);
  useEscClose(() => {
    setShowDelete(false);
    document.activeElement?.blur();
  }, showDelete);

  // Filtros aplicados (los únicos que afectan al backend)
  const [filtros, setFiltros] = useState({
    tarjetaId: "", // TARJ_SQ_COD
    tarjetaNumero: "",
    productoId: "", // PROD_SQ_COD
    productoCodigo: "", // compat
  });

  const [totalElementos, setTotalElementos] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);

  const devRunRef = useRef(false);

  // ==== helper: de dónde leer el token ====
  const getAuthToken = () =>
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    "";

  // ======== Fetch listado (paginación backend) ========
  const obtenerListado = async (
    pageIndex = 0,
    size = porPagina,
    filtrosArg = filtros,
  ) => {
    setCargando(true);
    try {
      const params = new URLSearchParams();
      params.set("page", pageIndex); // backend 0-based
      params.set("size", size);

      // Prioridad: tarjetaId → tarjetaNumero
      if (filtrosArg.tarjetaId) {
        params.set("tarjetaId", String(filtrosArg.tarjetaId));
      } else if (filtrosArg.tarjetaNumero?.trim()) {
        params.set("tarjetaNumero", filtrosArg.tarjetaNumero.trim());
      }

      // Prioridad: productoId → productoCodigo
      if (filtrosArg.productoId) {
        params.set("productoId", String(filtrosArg.productoId));
      } else if (filtrosArg.productoCodigo?.trim()) {
        params.set("productoCodigo", filtrosArg.productoCodigo.trim());
      }

      const url = `${apiRoutes.productoTarjeta.list}?${params.toString()}`;

      // forzamos Authorization por si el helper no lo agrega en cross-origin
      const token = getAuthToken();
      const data = await appFetchJson(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      const itemsResp = data?.items || data?.content || [];
      const total = Number.isFinite(data?.totalElements)
        ? data.totalElements
        : Number.isFinite(data?.total)
          ? data.total
          : itemsResp.length;

      setItems(itemsResp);
      setTotalElementos(total);
      setTotalPaginas(
        Number.isFinite(data?.totalPages)
          ? data.totalPages
          : Math.max(1, Math.ceil(total / (size || 1))),
      );
    } catch (error) {
      console.error("Error al cargar Productos por Tarjeta:", error);
      setItems([]);
      setTotalElementos(0);
      setTotalPaginas(0);
    } finally {
      setCargando(false);
    }
  };

  // Carga inicial (evita doble fetch en StrictMode)
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      if (!devRunRef.current) {
        devRunRef.current = true;
        return;
      }
    }
    obtenerListado(0, porPagina, filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers UI
  const manejarCambioCantidad = (e) => {
    const nuevoSize = Number(e.target.value);
    setPorPagina(nuevoSize);
    setPaginaActual(1);
    obtenerListado(0, nuevoSize, filtros);
  };

  const toggleFiltros = () => setMostrarFiltros(!mostrarFiltros);

  // 👇 YA NO disparamos el backend cuando cambia algo en el panel
  // (el panel maneja su estado local; acá solo “miramos” si queremos)
  const handleFiltroChange = (_snapshot) => {
    // intencionalmente vacío: no hay fetch “en vivo”
    // (si querés ver chips “en borrador”, podés guardar esto aparte)
  };

  // Se aplica SOLO acá
  const handleFiltroSubmit = (payload) => {
    if (!payload || Object.keys(payload).length === 0) {
      limpiarSeleccion();
      setMostrarFiltros(false);
      return;
    }
    const nuevosFiltros = { ...filtros, ...payload };
    setFiltros(nuevosFiltros);
    setPaginaActual(1);
    obtenerListado(0, porPagina, nuevosFiltros);
    setMostrarFiltros(false);
  };

  const cambiarPagina = (num) => {
    if (num >= 1 && num <= totalPaginas) {
      setPaginaActual(num);
      obtenerListado(num - 1, porPagina, filtros);
    }
  };

  const limpiarSeleccion = () => {
    const filtrosLimpios = {
      tarjetaId: "",
      tarjetaNumero: "",
      productoId: "",
      productoCodigo: "",
    };
    setFiltros(filtrosLimpios);
    setPaginaActual(1);
    obtenerListado(0, porPagina, filtrosLimpios);
  };

  const limpiarChip = (key) => {
    const next = { ...filtros, [key]: "" };
    if (key === "tarjetaId") next.tarjetaNumero = "";
    setFiltros(next);
    setPaginaActual(1);
    obtenerListado(0, porPagina, next);
  };

  const hayFiltros =
    (!!filtros.tarjetaId && String(filtros.tarjetaId).trim() !== "") ||
    (!!filtros.tarjetaNumero && filtros.tarjetaNumero.trim() !== "") ||
    (!!filtros.productoId && String(filtros.productoId).trim() !== "") ||
    (!!filtros.productoCodigo && filtros.productoCodigo.trim() !== "");

  const inicioMostrado =
    totalElementos === 0 ? 0 : (paginaActual - 1) * porPagina + 1;
  const finMostrado = Math.min(paginaActual * porPagina, totalElementos);

  const chipStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "999px",
    border: "1px solid #e5e7eb",
    background: "#f3f4f6",
    fontSize: 12,
  };
  const chipXStyle = {
    lineHeight: 1,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 16,
  };

  return (
    <div className="page">
      <h1 className="page-title">🧾 Productos por Tarjeta</h1>

      <div className="toolbar">
        <div className="inline-flex items-center gap-2">
          <button onClick={toggleFiltros} className="btn-fil">
            {mostrarFiltros ? "Ocultar Filtros" : "Ver Filtros"}
          </button>
          {hayFiltros && (
            <button onClick={limpiarSeleccion} className="btn-fil">
              Limpiar Selección
            </button>
          )}
        </div>
      </div>

      {/* Chips de filtros aplicados */}
      {hayFiltros && (
        <div className="mt-2 flex flex-wrap gap-2">
          {/* {!!filtros.tarjetaId && (
            <span style={chipStyle}>
              Tarjeta: {filtros.tarjetaNumero || `#${filtros.tarjetaId}`}
              <button
                type="button"
                title="Quitar tarjeta"
                style={chipXStyle}
                onClick={() => limpiarChip("tarjetaId")}
              >
                &times;
              </button>
            </span>
          )} */}
          {!!filtros.productoId && (
            <span style={chipStyle}>
              Producto ID: {filtros.productoId}
              <button
                type="button"
                title="Quitar producto"
                style={chipXStyle}
                onClick={() => limpiarChip("productoId")}
              >
                &times;
              </button>
            </span>
          )}
          {!!filtros.productoCodigo && !filtros.productoId && (
            <span style={chipStyle}>
              Producto: {filtros.productoCodigo}
              <button
                type="button"
                title="Quitar producto"
                style={chipXStyle}
                onClick={() => limpiarChip("productoCodigo")}
              >
                &times;
              </button>
            </span>
          )}
        </div>
      )}

      {mostrarFiltros && (
        <FiltroProductos
          initial={filtros}
          onChange={handleFiltroChange} // ← ya NO dispara fetch
          onSubmit={handleFiltroSubmit} // ← SOLO acá se aplica
          onClose={toggleFiltros}
        />
      )}

      {/* Mostrar por página */}
      <div className="control-block">
        <label htmlFor="cantidadPorPagina" className="label-strong">
          Mostrar por página:
        </label>
        <select
          id="cantidadPorPagina"
          value={porPagina}
          onChange={manejarCambioCantidad}
          className="select"
        >
          <option value={30}>30</option>
          <option value={40}>40</option>
          <option value={50}>50</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="table-card table-sm">
          <thead className="table-head">
            <tr>
              <th className="cell text-left">Tarjeta</th>
              <th className="cell text-left">Producto</th>
              <th className="cell-center">Acción</th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr>
                <td colSpan="3" className="cell-center py-4">
                  Cargando...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan="3" className="cell-center text-gray-500 py-4">
                  Ningún resultado encontrado.
                </td>
              </tr>
            ) : (
              items.map((row, index) => {
                const productoTxt =
                  (row.productoCodigo ? `${row.productoCodigo} - ` : "") +
                  (row.productoNombre ?? "-");
                return (
                  <tr key={row.id ?? index} className="row-border">
                    <td className="cell">{row.tarjetaNumero ?? "-"}</td>
                    <td className="cell">{productoTxt}</td>
                    <td className="cell-center space-x-2">
                      <button
                        type="button"
                        className="icon-btn"
                        title="Ver"
                        onClick={(e) => {
                          e.stopPropagation();
                          setItemSel(row); // contiene row.id
                          setShowVer(true);
                        }}
                      >
                        <FaEye />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        title="Eliminar"
                        onClick={(e) => {
                          e.stopPropagation();
                          setItemDelete(row);
                          setShowDelete(true);
                        }}
                      >
                        <FaTrashAlt />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        {`${totalElementos.toLocaleString("es-ES")} item${
          totalElementos !== 1 ? "s" : ""
        } encontrados, mostrando de ${inicioMostrado} a ${finMostrado}.`}
      </div>

      <nav className="pager">
        <button
          onClick={() => cambiarPagina(1)}
          disabled={paginaActual === 1}
          className={paginaActual === 1 ? "pager-off" : "pager-btn"}
        >
          Primera
        </button>
        <button
          onClick={() => cambiarPagina(paginaActual - 1)}
          disabled={paginaActual === 1}
          className={paginaActual === 1 ? "pager-off" : "pager-btn"}
        >
          Anterior
        </button>

        {[...Array(totalPaginas)].map((_, i) => {
          const num = i + 1;
          if (
            num === 1 ||
            num === totalPaginas ||
            (num >= paginaActual - 2 && num <= paginaActual + 2)
          ) {
            return (
              <button
                key={num}
                onClick={() => cambiarPagina(num)}
                className={paginaActual === num ? "pager-active" : "pager-btn"}
              >
                {num}
              </button>
            );
          }
          if (num === paginaActual - 3 || num === paginaActual + 3) {
            return (
              <span key={num} className="px-2 py-1">
                …
              </span>
            );
          }
          return null;
        })}

        <button
          onClick={() => cambiarPagina(paginaActual + 1)}
          disabled={paginaActual === totalPaginas || totalPaginas === 0}
          className={
            paginaActual === totalPaginas || totalPaginas === 0
              ? "pager-off"
              : "pager-btn"
          }
        >
          Próxima
        </button>
        <button
          onClick={() => cambiarPagina(totalPaginas)}
          disabled={paginaActual === totalPaginas || totalPaginas === 0}
          className={
            paginaActual === totalPaginas || totalPaginas === 0
              ? "pager-off"
              : "pager-btn"
          }
        >
          Última
        </button>
      </nav>

      {/* VER (modal) */}
      {showVer && (
        <div className="modal-backdrop" onClick={() => setShowVer(false)}>
          <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
            <VerProductosTarjeta
              registro={itemSel} // ← trae row.id
              onClose={() => setShowVer(false)}
            />
          </div>
        </div>
      )}

      {/* ELIMINAR (modal) */}
      {showDelete && (
        <div className="modal-backdrop" onClick={() => setShowDelete(false)}>
          <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
            <EliminarProductos
              registro={itemDelete}
              onClose={() => setShowDelete(false)}
              onDeleted={() => {
                obtenerListado(
                  Math.max(0, paginaActual - 1),
                  porPagina,
                  filtros,
                );
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductosTarjeta;
